import {resolveHypermediaUrl, type DomainResolverFn} from '@seed-hypermedia/client'
import {UnpackedHypermediaId} from '@seed-hypermedia/client/hm-types'
import {getDocumentTitle} from '@shm/shared/content'
import type {UniversalClient} from '@shm/shared/universal-client'
import {hmId, isHypermediaScheme, isPublicGatewayLink, packHmId, unpackHmId} from '@shm/shared/utils/entity-id-url'
import {StateStream} from '@shm/shared/utils/stream'
import {Editor} from '@tiptap/core'
import {Fragment, Mark, MarkType, Slice} from '@tiptap/pm/model'
import {Plugin, PluginKey} from '@tiptap/pm/state'
import {Decoration, DecorationSet} from '@tiptap/pm/view'
import {find} from 'linkifyjs'
import {nanoid} from 'nanoid'
import {getLinkMenuItems} from '../../blocknote/core/extensions/LinkMenu/defaultLinkMenuItems'
import {linkMenuPluginKey} from '../../blocknote/core/extensions/LinkMenu/LinkMenuPlugin'

type PasteHandlerOptions = {
  universalClient?: UniversalClient
  domainResolver?: DomainResolverFn
  editor: Editor
  type: MarkType
  linkOnPaste?: boolean
  gwUrl: StateStream<string>
  checkWebUrl: (url: string) => Promise<any>
  /** When provided and a `hm://…#blockId[...]` URL is pasted into an empty
   * selection, this callback receives the resolved hm URL (with the resolved
   * version filled in when needed) so the host editor can insert an Embed
   * block instead of falling back to a link mark. */
  onPasteHypermediaBlockFragment?: (resolvedHmUrl: string) => void | boolean
}

export function pasteHandler(options: PasteHandlerOptions): Plugin {
  let pastePlugin = new Plugin({
    key: new PluginKey('handlePasteLink'),
    state: {
      init() {
        return DecorationSet.empty
      },
      apply(tr, set) {
        // Adjust decoration positions to changes made by the transaction
        set = set.map(tr.mapping, tr.doc)
        // See if the transaction adds or removes any placeholders
        let action = tr.getMeta('link-placeholder')
        if (action && action.add) {
          let widget = document.createElement('span')
          widget.contentEditable = 'false'
          widget.classList.add('link-placeholder')
          let deco = Decoration.widget(action.add.pos, widget, {
            link: action.add.link,
          })
          set = set.add(tr.doc, [deco])
        } else if (action && action.remove) {
          set = set.remove(
            set.find(
              // @ts-expect-error
              null,
              null,
              (spec) => spec.link.href == action.remove.link.href,
            ),
          )
        }
        return set
      },
    },
    props: {
      decorations(state) {
        return this.getState(state)
      },
      transformPasted: (slice, view) => {
        // Add link marks to the pasted slice and return it to paste handlers
        const schema = view.state.schema
        const linkMarkType = options.type

        function addLinkMarksToFragment(fragment: Fragment, inCodeBlock: boolean = false): Fragment {
          const nodes: any[] = []

          fragment.forEach((node: any) => {
            const isCodeBlock = !!node.type?.spec?.code || node.type?.name === 'code-block'

            const isInCode = inCodeBlock || isCodeBlock

            if (node.type.name === 'text') {
              const text = node.text || ''

              if (isInCode) {
                nodes.push(schema.text(text)) // Don't add link marks to code blocks
                return
              }

              const links = find(text) || []

              if (links.length === 0) {
                nodes.push(node)
                return
              }

              // Build the content array with link marks
              let lastIndex = 0

              links.forEach((link: any) => {
                // Add text before the link to the content array
                if (link.start > lastIndex) {
                  const beforeText = text.slice(lastIndex, link.start)
                  if (beforeText) {
                    nodes.push(schema.text(beforeText, node.marks))
                  }
                }

                // Add the link to the content array
                const linkText = text.slice(link.start, link.end)
                if (linkText) {
                  const linkMark = linkMarkType.create({
                    href: link.href,
                  })
                  nodes.push(schema.text(linkText, [...node.marks, linkMark]))
                }

                lastIndex = link.end
              })

              // Add text after the link to the content array
              if (lastIndex < text.length) {
                const afterText = text.slice(lastIndex)
                if (afterText) {
                  nodes.push(schema.text(afterText, node.marks))
                }
              }
            } else if (node.content && node.content.size > 0) {
              // Process child content
              const transformedContent = addLinkMarksToFragment(node.content, isInCode)
              nodes.push(node.copy(transformedContent))
            } else {
              nodes.push(node)
            }
          })

          return Fragment.from(nodes)
        }

        // Return the transformed slice
        const transformedSlice = addLinkMarksToFragment(slice.content)
        return new Slice(transformedSlice, slice.openStart, slice.openEnd)
      },
      handlePaste: (view, _event, slice) => {
        const {state} = view
        const {selection} = state

        // Do not proceed if in code block.
        if (state.doc.resolve(selection.from).parent.type.spec.code) {
          return false
        }

        const pastedLinkMarks: Mark[] = []
        let textContent = ''

        slice.content.forEach((node) => {
          textContent += node.textContent

          node.marks.forEach((mark) => {
            if (mark.type.name === options.type.name) {
              pastedLinkMarks.push(mark)
            }
          })
        })

        textContent = textContent.trim()

        if (!textContent) {
          return false
        }

        const hasPastedLink = pastedLinkMarks.length > 0
        // const link = find(textContent).find(
        //   (item) => item.isLink && item.value === textContent,
        // )
        const matches = find(textContent)

        const candidate =
          matches.length === 1 &&
          // @ts-ignore
          matches[0].isLink &&
          // @ts-ignore
          textContent.trim().startsWith(matches[0].href)
            ? matches[0]
            : null
        const link = candidate
          ? {
              ...candidate,
              // @ts-ignore
              href: restoreBlockRangeSuffix(candidate.href, textContent.trim()),
            }
          : null

        const isSingleUrlText = !/\s/.test(textContent)
        const unpackedHmId =
          isSingleUrlText && (isHypermediaScheme(textContent) || isPublicGatewayLink(textContent, options.gwUrl))
            ? unpackHmId(textContent)
            : null

        if (!selection.empty && options.linkOnPaste) {
          const pastedLink = unpackedHmId
            ? packHmId(unpackedHmId)
            : hasPastedLink
            ? // @ts-ignore
              pastedLinkMarks[0].attrs.href
            : link?.href || null
          if (pastedLink) {
            if (unpackedHmId) {
              options.editor
                .chain()
                .setMark(options.type, {
                  href: pastedLink,
                })
                .run()
            } else {
              let id = nanoid(8)
              options.editor
                .chain()
                .command(({tr}) => {
                  tr.setMeta('hmPlugin:uncheckedLink', id)
                  return true
                })
                .setMark(options.type, {
                  href: pastedLink,
                  id,
                })
                .run()
            }
            return true
          }
        }

        const firstChildIsText = slice.content.firstChild?.type.name === 'text'
        const firstChildContainsLinkMark = slice.content.firstChild?.marks.some(
          (mark) => mark.type.name === options.type.name,
        )

        // If transformPasted already added a link mark, skip further processing
        // unless we have a link that might need special handling (twitter, instagram, video, etc)
        if (firstChildIsText && firstChildContainsLinkMark && !(link && selection.empty && !unpackedHmId)) {
          return false
        }

        if (selection.empty && unpackedHmId?.uid) {
          let tr = view.state.tr

          let pos = tr.selection.from
          if (options.universalClient) {
            fetchResourceTitle(unpackedHmId, options.universalClient, unpackedHmId.blockRef)
              .then(({title, version, type}) => {
                // When blockRef is present, use the fetched version to ensure
                // we reference the specific version containing the block
                const resolvedVersion = unpackedHmId.blockRef && !unpackedHmId.version ? version : unpackedHmId.version
                const normalizedHmUrl = packHmId(
                  hmId(unpackedHmId.uid, {
                    ...unpackedHmId,
                    version: resolvedVersion,
                  }),
                )
                // Block-fragment links → host can land them as Embed blocks.
                if (unpackedHmId.blockRef && options.onPasteHypermediaBlockFragment) {
                  const handled = options.onPasteHypermediaBlockFragment(normalizedHmUrl)
                  if (handled !== false) return
                }
                if (title) {
                  view.dispatch(
                    tr.insertText(title, pos).addMark(
                      pos,
                      pos + title.length,
                      options.editor.schema.mark('link', {
                        href: normalizedHmUrl,
                      }),
                    ),
                  )

                  view.dispatch(
                    view.state.tr.scrollIntoView().setMeta(linkMenuPluginKey, {
                      activate: true,
                      ref: normalizedHmUrl,
                      items: getLinkMenuItems({
                        isLoading: false,
                        hmId: unpackHmId(normalizedHmUrl),
                        sourceUrl: normalizedHmUrl,
                        title,
                        type,
                        gwUrl: options.gwUrl,
                      }),
                    }),
                  )
                } else {
                  view.dispatch(
                    tr.insertText(normalizedHmUrl, pos).addMark(
                      pos,
                      pos + normalizedHmUrl.length,
                      options.editor.schema.mark('link', {
                        href: normalizedHmUrl,
                      }),
                    ),
                  )

                  view.dispatch(
                    view.state.tr.scrollIntoView().setMeta(linkMenuPluginKey, {
                      activate: true,
                      ref: normalizedHmUrl,
                      items: getLinkMenuItems({
                        isLoading: false,
                        sourceUrl: normalizedHmUrl,
                        hmId: unpackHmId(normalizedHmUrl),
                        gwUrl: options.gwUrl,
                      }),
                    }),
                  )
                }
              })
              .catch((err) => {
                // Fallback: use original URL without resolved version
                const normalizedHmUrl = packHmId(hmId(unpackedHmId.uid, unpackedHmId))
                if (unpackedHmId.blockRef && options.onPasteHypermediaBlockFragment) {
                  const handled = options.onPasteHypermediaBlockFragment(normalizedHmUrl)
                  if (handled !== false) return
                }
                view.dispatch(
                  tr.insertText(normalizedHmUrl, pos).addMark(
                    pos,
                    pos + normalizedHmUrl.length,
                    options.editor.schema.mark('link', {
                      href: normalizedHmUrl,
                    }),
                  ),
                )

                view.dispatch(
                  view.state.tr.scrollIntoView().setMeta(linkMenuPluginKey, {
                    activate: true,
                    ref: normalizedHmUrl,
                    items: getLinkMenuItems({
                      isLoading: false,
                      sourceUrl: normalizedHmUrl,
                      hmId: unpackHmId(normalizedHmUrl),
                      gwUrl: options.gwUrl,
                    }),
                  }),
                )
              })

            return true
          }
        }

        // Check if the link is hm link or web URL
        if (selection.empty && link && !unpackedHmId) {
          // Capture position before async work, since document state may change.
          const insertPos = view.state.selection.$from.pos

          // Synchronously insert the raw URL with a link mark.
          // - The hm-resolved path overwrites it with the document title.
          // - The web path keeps it.
          // - handleWebUrl's internal web case already
          //   assumes the URL is in the doc and will delete+replace if it later
          //   resolves to an hm resource.
          const insertWebUrl = () => {
            view.dispatch(
              view.state.tr
                .insertText(link.href, insertPos)
                .addMark(
                  insertPos,
                  insertPos + link.href.length,
                  options.editor.schema.mark('link', {href: link.href}),
                ),
            )
          }

          ;(async () => {
            try {
              const linkMetaResult = await resolveHypermediaUrl(link.href, {domainResolver: options.domainResolver})

              if (linkMetaResult?.hmId) {
                const fullHmUrl = packHmId(linkMetaResult.hmId)

                // If title is missing (e.g. domain store resolution), fetch it
                let title = linkMetaResult.title
                let type: 'Comment' | 'Document' = linkMetaResult.type === 'Comment' ? 'Comment' : 'Document'
                if (!title && options.universalClient) {
                  const fetched = await fetchResourceTitle(
                    linkMetaResult.hmId,
                    options.universalClient,
                    linkMetaResult.hmId.blockRef,
                  )
                  title = fetched.title
                  type = fetched.type || type
                }

                const displayText = title || fullHmUrl

                let tr = view.state.tr

                view.dispatch(
                  tr
                    .insertText(displayText, insertPos)
                    .addMark(
                      insertPos,
                      insertPos + displayText.length,
                      options.editor.schema.mark('link', {
                        href: fullHmUrl,
                      }),
                    )
                    .scrollIntoView(),
                )

                view.dispatch(
                  view.state.tr.setMeta(linkMenuPluginKey, {
                    activate: true,
                    ref: fullHmUrl,
                    items: getLinkMenuItems({
                      isLoading: false,
                      hmId: linkMetaResult.hmId,
                      sourceUrl: fullHmUrl,
                      title,
                      type,
                      gwUrl: options.gwUrl,
                    }),
                  }),
                )
              } else {
                insertWebUrl()
                handleWebUrl(view, link, options)
              }
            } catch (err) {
              console.log('Error checking for hypermedia site:', err)
              insertWebUrl()
              handleWebUrl(view, link, options)
            }
          })()

          return true
        }

        return false
      },
    },
  })

  function checkMediaUrl(url: string): ['file' | 'image' | 'video' | 'web' | 'twitter' | 'instagram', string] {
    const matchResult = url.match(/[^/\\&\?]+\.\w{3,4}(?=([\?&].*$|$))/)
    if (matchResult) {
      const extensionArray = matchResult[0].split('.')
      const extension = extensionArray[extensionArray.length - 1]?.toLowerCase()

      if (extension && ['png', 'jpg', 'jpeg'].includes(extension)) {
        return ['image', matchResult[0]]
      } else if (extension && ['pdf', 'xml', 'csv'].includes(extension)) {
        return ['file', matchResult[0]]
      } else if (extension && ['mp4', 'webm', 'ogg'].includes(extension)) {
        return ['video', matchResult[0]]
      }
    } else if (['youtu.be', 'youtube', 'vimeo'].some((value) => url.includes(value))) {
      return ['video', '']
    } else if (['twitter', 'x.com'].some((value) => url.includes(value))) {
      return ['twitter', '']
    } else if (['instagram'].some((value) => url.includes(value))) {
      return ['instagram', '']
    }

    return ['web', '']
  }

  function handleWebUrl(view: any, link: any, options: PasteHandlerOptions) {
    const [mediaCase, fileName] = checkMediaUrl(link.href)

    view.dispatch(
      view.state.tr.scrollIntoView().setMeta(linkMenuPluginKey, {
        activate: true,
        link: link.href,
        items: getLinkMenuItems({
          isLoading: true,
          gwUrl: options.gwUrl,
        }),
      }),
    )

    switch (mediaCase) {
      case 'image':
        view.dispatch(
          view.state.tr.setMeta(linkMenuPluginKey, {
            link: link.href,
            items: getLinkMenuItems({
              isLoading: false,
              media: 'image',
              fileName: fileName,
              gwUrl: options.gwUrl,
            }),
          }),
        )
        break
      case 'file':
        view.dispatch(
          view.state.tr.setMeta(linkMenuPluginKey, {
            link: link.href,
            items: getLinkMenuItems({
              isLoading: false,
              media: 'file',
              fileName: fileName,
              gwUrl: options.gwUrl,
            }),
          }),
        )
        break
      case 'video':
        view.dispatch(
          view.state.tr.setMeta(linkMenuPluginKey, {
            link: link.href,
            items: getLinkMenuItems({
              isLoading: false,
              media: 'video',
              sourceUrl: link.href,
              fileName: fileName,
              gwUrl: options.gwUrl,
            }),
          }),
        )
        break
      case 'twitter':
        view.dispatch(
          view.state.tr.setMeta(linkMenuPluginKey, {
            link: link.href,
            items: getLinkMenuItems({
              isLoading: false,
              media: 'twitter',
              sourceUrl: link.href,
              fileName: fileName,
              gwUrl: options.gwUrl,
            }),
          }),
        )
        break
      case 'instagram':
        view.dispatch(
          view.state.tr.setMeta(linkMenuPluginKey, {
            link: link.href,
            items: getLinkMenuItems({
              isLoading: false,
              media: 'instagram',
              sourceUrl: link.href,
              fileName: fileName,
              gwUrl: options.gwUrl,
            }),
          }),
        )
        break
      case 'web': {
        const metaPromise = resolveHypermediaUrl(link.href, {domainResolver: options.domainResolver})
          .then(async (linkMetaResult) => {
            if (!linkMetaResult?.hmId) return false
            const fullHmUrl = packHmId(linkMetaResult.hmId)
            const currentPos = view.state.selection.$from.pos - link.href.length

            // If title is missing (e.g. domain store resolution), fetch it
            let title = linkMetaResult.title
            let type: 'Comment' | 'Document' = linkMetaResult.type === 'Comment' ? 'Comment' : 'Document'
            if (!title && options.universalClient) {
              const fetched = await fetchResourceTitle(
                linkMetaResult.hmId,
                options.universalClient,
                linkMetaResult.hmId.blockRef,
              )
              title = fetched.title
              type = fetched.type || type
            }

            if (title) {
              view.dispatch(
                view.state.tr
                  .deleteRange(currentPos, currentPos + link.href.length)
                  .insertText(title, currentPos)
                  .addMark(
                    currentPos,
                    currentPos + title.length,
                    options.editor.schema.mark('link', {
                      href: fullHmUrl,
                    }),
                  ),
              )
            }

            view.dispatch(
              view.state.tr.setMeta(linkMenuPluginKey, {
                link: fullHmUrl,
                items: getLinkMenuItems({
                  hmId: linkMetaResult.hmId,
                  isLoading: false,
                  sourceUrl: fullHmUrl,
                  title,
                  type,
                  gwUrl: options.gwUrl,
                }),
              }),
            )
            return true
          })
          .catch((err) => {
            console.log('ERROR FETCHING web link')
            console.log(err)
          })
        metaPromise
          .then((embedResult) => {
            if (!embedResult) {
              view.dispatch(
                view.state.tr.setMeta(linkMenuPluginKey, {
                  items: getLinkMenuItems({
                    isLoading: false,
                    sourceUrl: link.href,
                    gwUrl: options.gwUrl,
                  }),
                }),
              )
            }
          })
          .catch((err) => {
            console.log(err)
          })
        break
      }
      default:
        break
    }
  }

  return pastePlugin
}

// List normalization function
function getPastedNodes(parent: any, editor: any): any[] {
  const nodes: any[] = []

  parent.forEach((node: any) => {
    if (node.type.name === 'blockChildren') {
      const prevContainer = nodes.pop()
      if (prevContainer) {
        // @ts-ignore
        const container = editor.schema.nodes['blockNode'].create(
          prevContainer.attrs,
          prevContainer.content.addToEnd(node),
        )
        nodes.push(container)
      }
    } else if (node.type.name !== 'blockNode') {
      let nodeToInsert = node
      if (node.type.name === 'text') {
        // @ts-ignore
        nodeToInsert = editor.schema.nodes.paragraph.create({}, node)
      }
      // @ts-ignore
      const container = editor.schema.nodes['blockNode'].create(null, nodeToInsert)
      nodes.push(container)
    } else if (node.firstChild?.type.name === 'blockChildren') {
      const prevContainer = nodes.pop()
      if (prevContainer) {
        // @ts-ignore
        const container = editor.schema.nodes['blockNode'].create(
          prevContainer.attrs,
          prevContainer.content.addToEnd(node.firstChild!),
        )
        nodes.push(container)
      } else {
        nodes.push(node.firstChild!)
      }
    } else {
      nodes.push(node)
    }
  })

  return nodes
}

/**
 * Recover the trailing block-range fragment (`[start:end]` or `+`) that
 * linkifyjs drops when it tokenizes a Hypermedia URL.
 *
 * Hypermedia encodes a block range as `#blockId[start:end]` or `#blockId+`,
 * but `[`/`]` are RFC 3986 gen-delims and aren't allowed in a URL fragment
 * unencoded — linkifyjs ends the URL token at the `[`. Without this, pasting
 * `https://site.example/path#blockId[20:52]` resolves to a link/embed scoped
 * to the whole block instead of the highlighted range.
 *
 * Only re-attaches when `href` already has a fragment, so unrelated trailing
 * brackets in plain web URLs (e.g. markdown-style references) aren't pulled
 * into the URL.
 */
export function restoreBlockRangeSuffix(href: string, fullText: string): string {
  if (!fullText.startsWith(href)) return href
  if (href.indexOf('#') === -1) return href
  const remainder = fullText.slice(href.length)
  const match = remainder.match(/^(\[\d+:\d+\]|\+)/)
  return match ? href + match[0] : href
}

async function fetchResourceTitle(
  hmId: UnpackedHypermediaId,
  universalClient: UniversalClient,
  blockRef?: string | null,
) {
  const isProfile = hmId.path?.[0] === ':profile'
  const resource = await universalClient.request('Resource', isProfile ? {...hmId, path: null} : hmId)

  if (resource.type === 'document') {
    const doc = resource.document
    let title
    if (blockRef) {
      const block = doc.content.find((blockNode) => blockNode.block?.id === blockRef)
      if (block?.block?.type === 'Heading') {
        title = block.block.text
      }
    }
    return {
      title: title || getDocumentTitle(doc),
      version: doc.version || null,
      type: 'Document' as const,
    }
  }

  if (resource.type === 'comment') {
    const authorName = await fetchAuthorName(resource.comment.author, universalClient)
    return {
      title: authorName ? `Comment from ${authorName}` : 'Comment',
      version: resource.comment.version || null,
      type: 'Comment' as const,
    }
  }

  return {title: null, version: null, type: undefined}
}

async function fetchAuthorName(authorUid: string, universalClient: UniversalClient) {
  try {
    const authorResource = await universalClient.request('Resource', hmId(authorUid))
    if (authorResource.type !== 'document') return null
    const name = getDocumentTitle(authorResource.document)
    return name || null
  } catch (error) {
    return null
  }
}
